import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import { Edit, Share, Download, Image, Users, Clock, Eye, Copy, Check, Trash2, AlertTriangle, X, BarChart3, TrendingUp, Calendar, Upload, Globe, ExternalLink, RotateCcw, RefreshCw, Facebook, Twitter, Mail, Link as LinkIcon, Loader2, CheckSquare, Square, ChevronLeft, ChevronRight, Send, UserPlus, Phone } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { useToast } from '@/hooks/use-toast'
import JSZip from 'jszip'
import {
  getDropByIdWithAccess,
  deleteDrop as deleteDropFromStore,
  updateDrop,
  getUploadActivityByPeriod,
  getUploadStats,
  getContributorsByDrop,
  regenerateContributorToken,
  enablePublicGallery,
  disablePublicGallery,
  FREE_TIER_RETENTION_DAYS,
  type Drop,
  type ActivityByPeriod,
  type UploadStats,
  type ContributorData,
  type DropAccessError,
} from '@/lib/dropsStore'
import { getPhotosByDropId, deletePhoto, type Photo } from '@/lib/photosStore'
import { useAuth } from '@/contexts/AuthContext'
import { apiDeleteDrop } from '@/lib/apiService'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { useDropQuery, useDropPhotosQuery } from '@/data/drops'
import { useDropPhotosSubscription, useDropSubscription, useDropActivitySubscription } from '@/data/subscriptions'
import { useQueryClient } from '@tanstack/react-query'
import { PhotoGrid } from '@/components/PhotoGrid'
import { ContributorInvitationsSection } from '@/components/ContributorInvitationsSection'
import {
  getInvitationsByDropId,
  createInvitation,
  resendInvitation,
  cancelInvitation,
  type ContributorInvitation,
  type InvitationMethod,
} from '@/lib/contributorInvitationsStore'
import { ActivityFeed } from '@/components/ActivityFeed'
import { SessionActivityFeed } from '@/components/SessionActivityFeed'

export default function DropDetailPage() {
  return DEMO_AUTH_ENABLED ? <DemoDropDetailPage /> : <RealDropDetailPage />
}

function DemoDropDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { organization } = useAuth()
  const isFreeTier = organization?.tier === 'free'
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<'photos' | 'contributors' | 'analytics' | 'activity' | 'settings'>('photos')
  const [uploadStats, setUploadStats] = useState<UploadStats | null>(null)
  const [activityData, setActivityData] = useState<ActivityByPeriod[]>([])
  const [activityPeriod, setActivityPeriod] = useState<'hour' | 'day' | 'week'>('day')
  const [contributors, setContributors] = useState<ContributorData[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [showExtendTimeModal, setShowExtendTimeModal] = useState(false)
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [drop, setDrop] = useState<Drop | null>(null)
  const [accessError, setAccessError] = useState<DropAccessError | null>(null)
  const [loading, setLoading] = useState(true)
  const [photos, setPhotos] = useState<Photo[]>([])
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set())
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [isDownloadingSelected, setIsDownloadingSelected] = useState(false)
  // Invited contributors state
  const [invitedContributors, setInvitedContributors] = useState<ContributorInvitation[]>([])
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteMethod, setInviteMethod] = useState<InvitationMethod>('email')
  const [inviteEmail, setInviteEmail] = useState('')
  const [invitePhone, setInvitePhone] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [isInviting, setIsInviting] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)

  // Load drop data from shared store with access control
  useEffect(() => {
    const loadDrop = () => {
      if (!id) {
        setLoading(false)
        return
      }

      const result = getDropByIdWithAccess(id)
      if (result.error) {
        setAccessError(result.error)
        setDrop(null)
      } else {
        setDrop(result.drop)
        setAccessError(null)
      }
      setLoading(false)

      // Load upload stats, activity, contributors, and photos
      if (result.drop) {
        setUploadStats(getUploadStats(id))
        setActivityData(getUploadActivityByPeriod(id, activityPeriod))
        setContributors(getContributorsByDrop(id))
        setPhotos(getPhotosByDropId(id))
        setInvitedContributors(getInvitationsByDropId(id))
      }
    }

    loadDrop()

    // Refresh drop data every 5 seconds to get updated view counts
    const interval = setInterval(loadDrop, 5000)
    return () => clearInterval(interval)
  }, [id, activityPeriod])

  // Lightbox navigation functions
  const navigateToPhoto = useCallback((direction: 'prev' | 'next') => {
    if (!selectedPhoto || photos.length === 0) return
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id)
    if (currentIndex === -1) return

    let newIndex: number
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1
    } else {
      newIndex = currentIndex === photos.length - 1 ? 0 : currentIndex + 1
    }
    setSelectedPhoto(photos[newIndex])
  }, [selectedPhoto, photos])

  const closeLightbox = useCallback(() => {
    setSelectedPhoto(null)
  }, [])

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!selectedPhoto) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowLeft':
          navigateToPhoto('prev')
          break
        case 'ArrowRight':
          navigateToPhoto('next')
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedPhoto, navigateToPhoto, closeLightbox])

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading drop...</p>
        </div>
      </div>
    )
  }

  // Show access denied state (403)
    if (accessError?.code === 'ACCESS_DENIED') {
      return (
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <div className="h-16 w-16 bg-destructive/15 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="mt-6 text-2xl font-bold">Access Denied</h1>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">
              You do not have permission to access this drop. It may belong to a different organization.
            </p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button asChild>
              <Link to="/drops">View Your Drops</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Show not found state for invalid/deleted drops (404)
  if (!drop) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Image className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Drop not found</h1>
          <p className="mt-2 text-muted-foreground max-w-md mx-auto">
            This drop may have been deleted or does not exist.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button asChild>
              <Link to="/drops">View All Drops</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const shareLink = `${window.location.origin}/c/${drop.contributorToken}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      toast({
        title: 'Link copied',
        description: 'Contributor link copied to clipboard',
      })
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      toast({
        title: 'Failed to copy',
        description: 'Could not copy link to clipboard',
        variant: 'destructive',
      })
    }
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const handleDeleteDrop = async () => {
    // Check permissions via API service first
    const apiResult = apiDeleteDrop(drop.id)

    // Handle 403 Forbidden
    if (apiResult.status === 403) {
      toast({
        title: '403 Forbidden - Permission Denied',
        description: apiResult.error || 'You do not have permission to delete drops.',
        variant: 'destructive',
      })
      setShowDeleteModal(false)
      return
    }

    // Handle 401 Unauthorized
    if (apiResult.status === 401) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to delete drops.',
        variant: 'destructive',
      })
      navigate('/login')
      return
    }

    // Delete from the shared store
    const deleted = deleteDropFromStore(drop.id)

    if (deleted) {
      toast({
        title: 'Drop deleted',
        description: 'The drop has been permanently deleted.',
      })
      navigate('/drops')
    } else {
      toast({
        title: 'Failed to delete',
        description: 'Could not delete the drop. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleReopenDrop = (days: number) => {
    const newExpiresAt = new Date()
    newExpiresAt.setDate(newExpiresAt.getDate() + days)

    const updated = updateDrop(drop.id, {
      status: 'active',
      expiresAt: newExpiresAt,
    })

    if (updated) {
      setDrop(updated)
      setShowReopenModal(false)
      toast({
        title: 'Drop reopened',
        description: `The drop is now active and will close in ${days} day${days > 1 ? 's' : ''}.`,
      })
    } else {
      toast({
        title: 'Failed to reopen',
        description: 'Could not reopen the drop. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleExtendTime = (days: number) => {
    // Calculate new expiry date based on current expiry or now if not set
    const baseDate = drop.expiresAt ? new Date(drop.expiresAt) : new Date()
    const newExpiresAt = new Date(baseDate)
    newExpiresAt.setDate(newExpiresAt.getDate() + days)

    const updated = updateDrop(drop.id, {
      expiresAt: newExpiresAt,
    })

    if (updated) {
      setDrop(updated)
      setShowExtendTimeModal(false)
      toast({
        title: 'Time extended',
        description: `The drop will now close on ${newExpiresAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`,
      })
    } else {
      toast({
        title: 'Failed to extend time',
        description: 'Could not extend the drop time. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleRegenerateLink = () => {
    const result = regenerateContributorToken(drop.id)
    if (result) {
      setDrop(result.drop)
      toast({
        title: 'Link regenerated',
        description: 'A new contributor link has been generated. The old link is now invalid.',
      })
    } else {
      toast({
        title: 'Failed to regenerate',
        description: 'Could not regenerate the link. Please try again.',
        variant: 'destructive',
      })
    }
  }

  // Download all photos as zip
  const handleDownloadAll = async () => {
    if (photos.length === 0) {
      toast({
        title: 'No photos to download',
        description: 'This drop has no photos yet.',
        variant: 'destructive',
      })
      return
    }

    setIsDownloading(true)
    try {
      const zip = new JSZip()

      // Add each photo to the zip
      for (const photo of photos) {
        const imageData = photo.originalUrl || photo.thumbnailUrl
        if (!imageData) continue

        // Extract base64 data from data URL
        const base64Data = imageData.split(',')[1]
        if (!base64Data) continue

        // Determine file extension from data URL
        let extension = 'png'
        if (imageData.includes('image/jpeg')) {
          extension = 'jpg'
        } else if (imageData.includes('image/webp')) {
          extension = 'webp'
        }

        // Use original filename or generate one
        const filename = photo.filename.includes('.')
          ? photo.filename
          : `${photo.filename}.${extension}`

        zip.file(filename, base64Data, { base64: true })
      }

      // Generate and download the zip file
      const content = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(content)
      const link = document.createElement('a')
      link.href = url
      link.download = `${drop.name.replace(/[^a-z0-9]/gi, '_')}_photos.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast({
        title: 'Download started',
        description: `Downloading ${photos.length} photos as zip.`,
      })
    } catch (err) {
      console.error('Failed to create zip:', err)
      toast({
        title: 'Download failed',
        description: 'Could not create zip file. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  // Toggle selection of a single photo
  const togglePhotoSelection = (photoId: string) => {
    setSelectedPhotoIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(photoId)) {
        newSet.delete(photoId)
      } else {
        newSet.add(photoId)
      }
      return newSet
    })
  }

  // Select/deselect all photos
  const handleSelectAll = () => {
    if (selectedPhotoIds.size === photos.length) {
      // All selected, deselect all
      setSelectedPhotoIds(new Set())
    } else {
      // Select all
      setSelectedPhotoIds(new Set(photos.map(p => p.id)))
    }
  }

  // Download selected photos as zip
  const handleDownloadSelected = async () => {
    const selectedPhotos = photos.filter(p => selectedPhotoIds.has(p.id))
    if (selectedPhotos.length === 0) {
      toast({
        title: 'No photos selected',
        description: 'Please select photos to download.',
        variant: 'destructive',
      })
      return
    }

    setIsDownloadingSelected(true)
    try {
      const zip = new JSZip()

      // Add each selected photo to the zip
      for (const photo of selectedPhotos) {
        const imageData = photo.originalUrl || photo.thumbnailUrl
        if (!imageData) continue

        // Extract base64 data from data URL
        const base64Data = imageData.split(',')[1]
        if (!base64Data) continue

        // Determine file extension from data URL
        let extension = 'png'
        if (imageData.includes('image/jpeg')) {
          extension = 'jpg'
        } else if (imageData.includes('image/webp')) {
          extension = 'webp'
        }

        // Use original filename or generate one
        const filename = photo.filename.includes('.')
          ? photo.filename
          : `${photo.filename}.${extension}`

        zip.file(filename, base64Data, { base64: true })
      }

      // Generate and download the zip file
      const content = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(content)
      const link = document.createElement('a')
      link.href = url
      link.download = `${drop.name.replace(/[^a-z0-9]/gi, '_')}_selected_photos.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast({
        title: 'Download started',
        description: `Downloading ${selectedPhotos.length} selected photo${selectedPhotos.length > 1 ? 's' : ''} as zip.`,
      })

      // Clear selection after download
      setSelectedPhotoIds(new Set())
      setIsSelectMode(false)
    } catch (err) {
      console.error('Failed to create zip:', err)
      toast({
        title: 'Download failed',
        description: 'Could not create zip file. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsDownloadingSelected(false)
    }
  }

  // Handle inviting a contributor
  const handleInviteContributor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    // Validate input
    if (inviteMethod === 'email' && (!inviteEmail || !inviteEmail.includes('@'))) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      })
      return
    }
    if (inviteMethod === 'sms' && !invitePhone) {
      toast({
        title: 'Invalid phone number',
        description: 'Please enter a valid phone number.',
        variant: 'destructive',
      })
      return
    }

    setIsInviting(true)
    try {
      const invitation = createInvitation(id, inviteMethod, {
        email: inviteMethod === 'email' ? inviteEmail : undefined,
        phone: inviteMethod === 'sms' ? invitePhone : undefined,
        name: inviteName || undefined,
      })

      setInvitedContributors(getInvitationsByDropId(id))
      setShowInviteModal(false)
      setInviteEmail('')
      setInvitePhone('')
      setInviteName('')

      toast({
        title: 'Invitation sent',
        description: `Invitation sent to ${invitation.recipientName || invitation.recipientEmail || invitation.recipientPhone}`,
      })
    } catch (error) {
      toast({
        title: 'Failed to send invitation',
        description: error instanceof Error ? error.message : 'An error occurred.',
        variant: 'destructive',
      })
    } finally {
      setIsInviting(false)
    }
  }

  // Handle resending an invitation
  const handleResendInvitation = async (invitationId: string) => {
    setResendingId(invitationId)
    try {
      const updated = resendInvitation(invitationId)
      if (updated) {
        setInvitedContributors(getInvitationsByDropId(id!))
        toast({
          title: 'Invitation resent',
          description: `Invitation resent to ${updated.recipientName || updated.recipientEmail || updated.recipientPhone}`,
        })
      }
    } catch (error) {
      toast({
        title: 'Failed to resend invitation',
        description: error instanceof Error ? error.message : 'An error occurred.',
        variant: 'destructive',
      })
    } finally {
      setResendingId(null)
    }
  }

  // Handle canceling an invitation
  const handleCancelInvitation = (invitationId: string) => {
    if (confirm('Are you sure you want to cancel this invitation?')) {
      const success = cancelInvitation(invitationId)
      if (success) {
        setInvitedContributors(getInvitationsByDropId(id!))
        toast({
          title: 'Invitation cancelled',
          description: 'The invitation has been cancelled.',
        })
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Drops', href: '/drops' },
          { label: drop.name },
        ]}
      />

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{drop.name}</h1>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                drop.status === 'active'
                  ? 'bg-emerald-500/10 text-emerald-500'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {drop.status === 'active' ? 'Active' : 'Closed'}
            </span>
          </div>
          {drop.description && (
            <p className="text-muted-foreground mt-2 max-w-2xl">{drop.description}</p>
          )}
          <p className="text-sm text-muted-foreground mt-2">
            Created {formatDate(drop.createdAt)}
            {drop.expiresAt && ` · Expires ${formatDate(drop.expiresAt)}`}
          </p>
        </div>
        <div className="flex gap-2">
          {/* Reopen button - only show for closed drops */}
          {drop.status === 'closed' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowReopenModal(true)}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reopen Drop
            </Button>
          )}
          <div className="relative">
            <Button variant="outline" size="sm" onClick={() => setShowShareMenu(!showShareMenu)}>
              <Share className="mr-2 h-4 w-4" />
              Share
            </Button>

            {/* Share Menu Dropdown */}
            {showShareMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowShareMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-56 rounded-md border bg-popover text-popover-foreground shadow-lg z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        handleCopyLink()
                        setShowShareMenu(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                    >
                      <LinkIcon className="mr-3 h-4 w-4" />
                      Copy contributor link
                      {copied && <Check className="ml-auto h-4 w-4 text-green-600" />}
                    </button>
                    <div className="border-t my-1" />
                    <button
                      onClick={() => {
                        window.open(
                          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`,
                          '_blank',
                          'width=600,height=400'
                        )
                        setShowShareMenu(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                    >
                      <Facebook className="mr-3 h-4 w-4 text-blue-600" />
                      Share on Facebook
                    </button>
                    <button
                      onClick={() => {
                        window.open(
                          `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(`Upload your photos to ${drop.name}!`)}`,
                          '_blank',
                          'width=600,height=400'
                        )
                        setShowShareMenu(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                    >
                      <Twitter className="mr-3 h-4 w-4 text-sky-500" />
                      Share on Twitter/X
                    </button>
                    <button
                      onClick={() => {
                        window.location.href = `mailto:?subject=${encodeURIComponent(`Upload photos to ${drop.name}`)}&body=${encodeURIComponent(`I'd love for you to upload your photos!\n\n${shareLink}`)}`
                        setShowShareMenu(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                    >
                      <Mail className="mr-3 h-4 w-4 text-muted-foreground" />
                      Share via Email
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadAll}
            disabled={isDownloading || photos.length === 0}
          >
            {isDownloading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download All
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/drops/${id}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-card text-card-foreground rounded-lg border p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Image className="h-4 w-4" />
            <span className="text-sm">Photos</span>
          </div>
          <p className="text-2xl font-bold mt-1">{drop.photoCount}</p>
        </div>
        <div className="bg-card text-card-foreground rounded-lg border p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" />
            <span className="text-sm">Contributors</span>
          </div>
          <p className="text-2xl font-bold mt-1">{drop.contributorCount}</p>
        </div>
        <div className="bg-card text-card-foreground rounded-lg border p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Eye className="h-4 w-4" />
            <span className="text-sm">Views</span>
          </div>
          <p className="text-2xl font-bold mt-1">{drop.viewCount}</p>
        </div>
        <div className="bg-card text-card-foreground rounded-lg border p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="text-sm">Status</span>
          </div>
          <p className="text-2xl font-bold mt-1 capitalize">{drop.status}</p>
        </div>
      </div>

      {/* Retention Warning - Free Tier Closed Drops */}
      {isFreeTier && drop.status === 'closed' && (
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-foreground">Free Tier Retention Limit</h3>
              <p className="text-sm text-muted-foreground mt-1">
                On the Free plan, closed drops are retained for {FREE_TIER_RETENTION_DAYS} days.
                {drop.retentionExpiresAt ? (
                  <>
                    {' '}This drop will be permanently deleted on{' '}
                    <span className="font-medium">
                      {new Date(drop.retentionExpiresAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>.
                  </>
                ) : (
                  <> This drop and all photos will be deleted after the retention period.</>
                )}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <Link to="/settings/billing" className="font-medium text-primary hover:underline">
                  Upgrade to Pro or Business
                </Link>
                {' '}for perpetual retention and keep your photos forever.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Share Link */}
      <div className="bg-card text-card-foreground rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-foreground">Contributor Link</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Share this link with contributors to collect photos
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code className="bg-muted text-foreground px-3 py-2 rounded border border-border text-sm">{shareLink}</code>
            <Button size="sm" variant="outline" onClick={handleCopyLink}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRegenerateLink}
              title="Regenerate link (invalidates existing link)"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('photos')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'photos'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Photos ({drop.photoCount})
          </button>
          <button
            onClick={() => setActiveTab('contributors')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'contributors'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Contributors ({drop.contributorCount})
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'analytics'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'activity'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Activity
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 border-b-2 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'photos' && (
        photos.length > 0 ? (
          <div className="space-y-4">
            {/* Selection Toolbar */}
            <div className="bg-card text-card-foreground rounded-lg border p-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    if (isSelectMode) {
                      setIsSelectMode(false)
                      setSelectedPhotoIds(new Set())
                    } else {
                      setIsSelectMode(true)
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isSelectMode
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {isSelectMode ? (
                    <>
                      <X className="h-4 w-4" />
                      Cancel Selection
                    </>
                  ) : (
                    <>
                      <CheckSquare className="h-4 w-4" />
                      Select Photos
                    </>
                  )}
                </button>
                {isSelectMode && (
                  <>
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors"
                    >
                      {selectedPhotoIds.size === photos.length ? (
                        <>
                          <Square className="h-4 w-4" />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <CheckSquare className="h-4 w-4" />
                          Select All
                        </>
                      )}
                    </button>
                    <span className="text-sm text-muted-foreground">
                      {selectedPhotoIds.size} of {photos.length} selected
                    </span>
                  </>
                )}
              </div>
              {isSelectMode && selectedPhotoIds.size > 0 && (
                <Button
                  onClick={handleDownloadSelected}
                  disabled={isDownloadingSelected}
                  size="sm"
                >
                  {isDownloadingSelected ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Download Selected ({selectedPhotoIds.size})
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Photo Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {photos.map((photo) => {
                const isSelected = selectedPhotoIds.has(photo.id)
                return (
                  <div
                    key={photo.id}
                    className={`group relative bg-card rounded-lg border overflow-hidden cursor-pointer ${
                      isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                    }`}
                    onClick={() => {
                      if (isSelectMode) {
                        togglePhotoSelection(photo.id)
                      } else {
                        setSelectedPhoto(photo)
                      }
                    }}
                  >
                    <div className="aspect-square">
                      <img
                        src={photo.thumbnailUrl || photo.originalUrl}
                        alt={photo.filename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Selection checkbox - always visible in select mode */}
                    {isSelectMode && (
                      <div
                        className={`absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-primary text-white'
                            : 'bg-card/80 text-muted-foreground hover:bg-card backdrop-blur-sm'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          togglePhotoSelection(photo.id)
                        }}
                      >
                        {isSelected ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </div>
                    )}
                    {/* Hover actions - hidden in select mode */}
                    {!isSelectMode && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            // Download photo
                            const link = document.createElement('a')
                            link.href = photo.originalUrl || photo.thumbnailUrl || ''
                            link.download = photo.filename
                            link.click()
                          }}
                          className="p-2 bg-foreground/90 text-background rounded-full hover:bg-foreground transition-colors backdrop-blur-sm"
                          title="Download photo"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            // Delete photo
                            if (confirm(`Delete "${photo.filename}"?`)) {
                              deletePhoto(photo.id)
                              setPhotos(prev => prev.filter(p => p.id !== photo.id))
                              updateDrop(drop.id, {
                                photoCount: Math.max(0, photos.length - 1)
                              })
                              toast({
                                title: 'Photo deleted',
                                description: 'The photo has been removed.',
                              })
                            }
                          }}
                          className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          title="Delete photo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                        {photo.caption}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="bg-card text-card-foreground rounded-lg border p-12 text-center">
            <Image className="h-12 w-12 text-muted-foreground mx-auto" />
            <h3 className="mt-4 text-lg font-semibold">Waiting for photos</h3>
            <p className="mt-2 text-muted-foreground">
              Share your drop link to start receiving photos
            </p>
            <Button className="mt-6" variant="outline" onClick={handleCopyLink}>
              <Copy className="mr-2 h-4 w-4" />
              Copy Share Link
            </Button>
          </div>
        )
      )}

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white hover:text-white/70 z-10"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Previous button */}
          {photos.length > 1 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-white/70 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation()
                navigateToPhoto('prev')
              }}
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {/* Next button */}
          {photos.length > 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-white/70 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation()
                navigateToPhoto('next')
              }}
              aria-label="Next photo"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Photo counter */}
          {photos.length > 1 && (
            <div className="absolute top-4 left-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full z-10">
              {photos.findIndex(p => p.id === selectedPhoto.id) + 1} / {photos.length}
            </div>
          )}

          <div className="max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.originalUrl || selectedPhoto.thumbnailUrl}
              alt={selectedPhoto.filename}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-white text-center">
              <p className="font-medium">{selectedPhoto.filename}</p>
              {selectedPhoto.caption && (
                <p className="text-white/70 mt-1">{selectedPhoto.caption}</p>
              )}
              <p className="text-white/60 text-sm mt-2">
                Uploaded {new Date(selectedPhoto.uploadedAt).toLocaleString()}
              </p>
              {selectedPhoto.uploadedBy && (
                <p className="text-white/60 text-sm mt-1 flex items-center justify-center gap-1">
                  <Users className="h-3 w-3" />
                  <span>by {selectedPhoto.uploadedBy}</span>
                </p>
              )}
              <div className="mt-4 flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    const link = document.createElement('a')
                    link.href = selectedPhoto.originalUrl || selectedPhoto.thumbnailUrl || ''
                    link.download = selectedPhoto.filename
                    link.click()
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (confirm(`Delete "${selectedPhoto.filename}"?`)) {
                      deletePhoto(selectedPhoto.id)
                      setPhotos(prev => prev.filter(p => p.id !== selectedPhoto.id))
                      updateDrop(drop.id, {
                        photoCount: Math.max(0, photos.length - 1)
                      })
                      setSelectedPhoto(null)
                      toast({
                        title: 'Photo deleted',
                        description: 'The photo has been removed.',
                      })
                    }
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'contributors' && (
        <div className="space-y-6">
          {/* Invited Contributors Section */}
          <div className="bg-card text-card-foreground rounded-lg border">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Invited Contributors</h3>
                <p className="text-sm text-muted-foreground">
                  People you've invited to upload photos to this drop
                </p>
              </div>
              <Button size="sm" onClick={() => setShowInviteModal(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite Contributor
              </Button>
            </div>
            {invitedContributors.length > 0 ? (
              <div className="divide-y">
                {invitedContributors.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="px-6 py-4 flex items-center justify-between hover:bg-muted/20 transition-colors"
                  >
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          invitation.status === 'accepted'
                            ? 'bg-secondary/15'
                            : invitation.status === 'pending'
                              ? 'bg-accent/15'
                              : 'bg-muted'
                        }`}>
                          {invitation.method === 'email' ? (
                            <Mail className={`h-5 w-5 ${
                              invitation.status === 'accepted'
                                ? 'text-secondary'
                                : invitation.status === 'pending'
                                  ? 'text-accent'
                                  : 'text-muted-foreground'
                            }`} />
                          ) : (
                            <Phone className={`h-5 w-5 ${
                              invitation.status === 'accepted'
                                ? 'text-secondary'
                                : invitation.status === 'pending'
                                  ? 'text-accent'
                                  : 'text-muted-foreground'
                            }`} />
                          )}
                        </div>
                      <div>
                        <p className="font-medium">
                          {invitation.recipientName || invitation.recipientEmail || invitation.recipientPhone}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {invitation.recipientEmail || invitation.recipientPhone}
                          {invitation.resentCount > 0 && (
                            <span className="ml-2">· Resent {invitation.resentCount} time{invitation.resentCount !== 1 ? 's' : ''}</span>
                          )}
                        </p>
                      </div>
                    </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invitation.status === 'accepted'
                            ? 'bg-secondary/15 text-secondary'
                            : invitation.status === 'pending'
                              ? 'bg-accent/15 text-accent'
                              : 'bg-muted text-muted-foreground'
                        }`}>
                          {invitation.status === 'accepted' ? 'Accepted' : invitation.status === 'pending' ? 'Pending' : 'Expired'}
                        </span>
                      {invitation.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResendInvitation(invitation.id)}
                            disabled={resendingId === invitation.id}
                          >
                            {resendingId === invitation.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="mr-1 h-3 w-3" />
                                Resend
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleCancelInvitation(invitation.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Mail className="h-10 w-10 text-muted-foreground mx-auto" />
                <h4 className="mt-3 font-medium">No invitations sent</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Invite specific people to upload photos to this drop
                </p>
              </div>
            )}
          </div>

          {/* Anonymous Contributors Section */}
          <div className="bg-card text-card-foreground rounded-lg border">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Link Contributors</h3>
              <p className="text-sm text-muted-foreground">
                People who uploaded photos using the public contributor link
              </p>
            </div>
            {contributors.length > 0 ? (
              <div className="divide-y">
                {/* Contributors list header */}
                <div className="px-6 py-3 bg-muted/30 grid grid-cols-12 gap-4 text-sm font-medium text-muted-foreground">
                  <div className="col-span-4">Contributor</div>
                  <div className="col-span-2 text-center">Photos</div>
                  <div className="col-span-2 text-center">Uploads</div>
                  <div className="col-span-4">Last Upload</div>
                </div>

                {/* Contributors list */}
                {contributors.map((contributor) => (
                  <div
                    key={contributor.sessionId}
                    className="px-6 py-4 grid grid-cols-12 gap-4 items-center hover:bg-muted/20 transition-colors"
                  >
                    <div className="col-span-4 flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{contributor.displayName}</p>
                        <p className="text-xs text-muted-foreground">
                          ID: {contributor.sessionId.substring(0, 8)}...
                        </p>
                      </div>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-sm font-medium">
                        <Image className="h-3.5 w-3.5" />
                        {contributor.photoCount}
                      </span>
                    </div>
                    <div className="col-span-2 text-center text-sm text-muted-foreground">
                      {contributor.uploadCount} upload{contributor.uploadCount !== 1 ? 's' : ''}
                    </div>
                    <div className="col-span-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {contributor.lastUpload.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Users className="h-10 w-10 text-muted-foreground mx-auto" />
                <h4 className="mt-3 font-medium">No link contributors yet</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Contributors will appear here once they upload photos via the share link
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card text-card-foreground rounded-lg border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Upload className="h-4 w-4" />
                <span className="text-sm">Total Uploads</span>
              </div>
              <p className="text-2xl font-bold mt-1">{uploadStats?.totalUploads || 0}</p>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Image className="h-4 w-4" />
                <span className="text-sm">Photos Uploaded</span>
              </div>
              <p className="text-2xl font-bold mt-1">{uploadStats?.totalPhotos || 0}</p>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span className="text-sm">Unique Contributors</span>
              </div>
              <p className="text-2xl font-bold mt-1">{uploadStats?.uniqueContributors || 0}</p>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">Avg Photos/Upload</span>
              </div>
              <p className="text-2xl font-bold mt-1">{uploadStats?.averagePhotosPerUpload || 0}</p>
            </div>
          </div>

          {/* Upload Timeline */}
          <div className="bg-card text-card-foreground rounded-lg border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Upload Activity Timeline
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Track photo uploads over time
                </p>
              </div>
              <div className="flex gap-1">
                <Button
                  variant={activityPeriod === 'hour' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActivityPeriod('hour')}
                >
                  Hourly
                </Button>
                <Button
                  variant={activityPeriod === 'day' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActivityPeriod('day')}
                >
                  Daily
                </Button>
                <Button
                  variant={activityPeriod === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActivityPeriod('week')}
                >
                  Weekly
                </Button>
              </div>
            </div>

            {activityData.length > 0 ? (
              <div className="space-y-4">
                {/* Simple bar chart visualization */}
                <div className="space-y-2">
                  {activityData.map((item, index) => {
                    const maxPhotos = Math.max(...activityData.map(a => a.photos))
                    const barWidth = maxPhotos > 0 ? (item.photos / maxPhotos) * 100 : 0
                    return (
                      <div key={index} className="flex items-center gap-4">
                        <div className="w-24 text-sm text-muted-foreground truncate">
                          {item.period}
                        </div>
                        <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
                          <div
                            className="bg-primary h-full rounded-full transition-all flex items-center justify-end pr-2"
                            style={{ width: `${Math.max(barWidth, 5)}%` }}
                          >
                            <span className="text-xs text-white font-medium">
                              {item.photos}
                            </span>
                          </div>
                        </div>
                        <div className="w-16 text-sm text-muted-foreground text-right">
                          {item.uploads} upload{item.uploads !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Summary */}
                {uploadStats?.firstUpload && uploadStats?.lastUpload && (
                  <div className="pt-4 border-t flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        First upload: {uploadStats.firstUpload.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>
                        Last upload: {uploadStats.lastUpload.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto" />
                <h3 className="mt-4 text-lg font-semibold">No upload activity yet</h3>
                <p className="mt-2 text-muted-foreground">
                  Upload activity will appear here once contributors start uploading photos
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-card rounded-xl border p-5">
          <h3 className="font-semibold mb-4">Activity Feed</h3>
          <ActivityFeed dropId={drop.id} />
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Public Gallery Settings */}
          <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Public Gallery
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow anyone with the gallery link to view photos without logging in.
                  This is read-only access - visitors cannot upload or delete photos.
                </p>
              </div>
              <button
                onClick={() => {
                  if (drop.isPublicGalleryEnabled) {
                    disablePublicGallery(drop.id)
                    setDrop({ ...drop, isPublicGalleryEnabled: false })
                    toast({
                      title: 'Public gallery disabled',
                      description: 'The public gallery link is no longer accessible.',
                    })
                  } else {
                    const result = enablePublicGallery(drop.id)
                    if (result) {
                      setDrop({ ...drop, isPublicGalleryEnabled: true, publicGalleryToken: result.token })
                      toast({
                        title: 'Public gallery enabled',
                        description: 'Anyone with the link can now view your photos.',
                      })
                    }
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  drop.isPublicGalleryEnabled ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-foreground transition-transform ${
                    drop.isPublicGalleryEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Public gallery link - only show when enabled */}
            {drop.isPublicGalleryEnabled && drop.publicGalleryToken && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-foreground">Public Gallery Link</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Share this link for read-only access to the gallery
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-muted text-foreground px-3 py-2 rounded border border-border text-sm">
                      {`${window.location.origin}/gallery/${drop.publicGalleryToken}`}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            `${window.location.origin}/gallery/${drop.publicGalleryToken}`
                          )
                          toast({
                            title: 'Link copied',
                            description: 'Public gallery link copied to clipboard',
                          })
                        } catch (err) {
                          toast({
                            title: 'Failed to copy',
                            description: 'Could not copy link to clipboard',
                            variant: 'destructive',
                          })
                        }
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                    >
                      <a href={`/gallery/${drop.publicGalleryToken}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Extend Time Settings - Only show for active drops */}
          {drop.status === 'active' && (
            <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Extend Drop Time
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Need more time to collect photos? Extend the drop's closing date.
                    {drop.expiresAt && (
                      <span className="block mt-1 text-foreground font-medium">
                        Currently closes: {new Date(drop.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowExtendTimeModal(true)}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Extend Time
                </Button>
              </div>
            </div>
          )}

          {/* General Settings */}
          <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
            <div>
              <h3 className="font-medium">General Settings</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Configure your drop settings and permissions
              </p>
            </div>
            <div className="pt-4 border-t">
              <Button variant="outline" asChild>
                <Link to={`/drops/${id}/edit`}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Drop Settings
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteDropModal
          dropName={drop.name}
          photoCount={drop.photoCount}
          onConfirm={handleDeleteDrop}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* Reopen Drop Modal */}
      {showReopenModal && (
        <ReopenDropModal
          dropName={drop.name}
          onConfirm={handleReopenDrop}
          onCancel={() => setShowReopenModal(false)}
        />
      )}

      {/* Extend Time Modal */}
      {showExtendTimeModal && (
        <ExtendTimeModal
          dropName={drop.name}
          currentExpiresAt={drop.expiresAt}
          onConfirm={handleExtendTime}
          onCancel={() => setShowExtendTimeModal(false)}
        />
      )}

      {/* Invite Contributor Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowInviteModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
            <button
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              onClick={() => setShowInviteModal(false)}
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-3 text-primary mb-4">
              <UserPlus className="h-6 w-6" />
              <h2 className="text-lg font-semibold">Invite Contributor</h2>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Send a direct invitation to someone to upload photos to this drop.
            </p>

            <form onSubmit={handleInviteContributor}>
              {/* Invite Method Toggle */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Invite via</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setInviteMethod('email')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      inviteMethod === 'email'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:border-primary hover:bg-muted/30'
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setInviteMethod('sms')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      inviteMethod === 'sms'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:border-primary hover:bg-muted/30'
                    }`}
                  >
                    <Phone className="h-4 w-4" />
                    SMS
                  </button>
                </div>
              </div>

              {/* Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Name (optional)</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="e.g. Uncle Bob"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  disabled={isInviting}
                />
              </div>

              {/* Email or Phone Field */}
              {inviteMethod === 'email' ? (
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Email address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="contributor@example.com"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    disabled={isInviting}
                    required
                  />
                </div>
              ) : (
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Phone number</label>
                  <input
                    type="tel"
                    value={invitePhone}
                    onChange={(e) => setInvitePhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    disabled={isInviting}
                    required
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowInviteModal(false)}
                  disabled={isInviting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isInviting}
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

interface DeleteDropModalProps {
  dropName: string
  photoCount: number
  onConfirm: () => void
  onCancel: () => void
}

function DeleteDropModal({ dropName, photoCount, onConfirm, onCancel }: DeleteDropModalProps) {
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirm = async () => {
    setIsDeleting(true)
    await onConfirm()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-destructive mb-4">
          <AlertTriangle className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Delete Drop</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to delete <span className="font-medium text-foreground">"{dropName}"</span>?
        </p>

        <p className="text-sm text-muted-foreground mb-6">
          This action cannot be undone. All photos ({photoCount}) and contributor data will be permanently deleted.
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Drop'}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface ReopenDropModalProps {
  dropName: string
  onConfirm: (days: number) => void
  onCancel: () => void
}

function ReopenDropModal({ dropName, onConfirm, onCancel }: ReopenDropModalProps) {
  const [selectedDays, setSelectedDays] = useState<number>(7)

  const durationOptions = [
    { days: 1, label: '1 day' },
    { days: 3, label: '3 days' },
    { days: 7, label: '1 week' },
    { days: 14, label: '2 weeks' },
    { days: 30, label: '1 month' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-primary mb-4">
          <RotateCcw className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Reopen Drop</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Reopen <span className="font-medium text-foreground">"{dropName}"</span> to accept new photo uploads.
        </p>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            How long should the drop remain open?
          </label>
          <div className="grid grid-cols-5 gap-2">
            {durationOptions.map((option) => (
              <button
                key={option.days}
                onClick={() => setSelectedDays(option.days)}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  selectedDays === option.days
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary hover:bg-muted/30'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={() => onConfirm(selectedDays)}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reopen Drop
          </Button>
        </div>
      </div>
    </div>
  )
}

function RealDropDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { drop, isLoading: loading, error, refetch } = useDropQuery(id)
  const { photos, isLoading: photosLoading } = useDropPhotosQuery(drop?.id)

  // Realtime subscriptions — invalidate query caches on each push
  useDropPhotosSubscription(drop?.id, queryClient)
  useDropSubscription(id, queryClient, () => navigate('/drops'))
  useDropActivitySubscription(drop?.id, queryClient)

  const [copied, setCopied] = useState(false)

  const contributorUrl = drop?.defaultContributorToken
    ? `${window.location.origin}/c/${drop.defaultContributorToken}`
    : null

  const handleCopy = async () => {
    if (!contributorUrl) return
    try {
      await navigator.clipboard.writeText(contributorUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
      toast({ title: 'Copied', description: 'Contributor link copied to clipboard.' })
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy link.', variant: 'destructive' })
    }
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading drop...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold">Failed to load drop</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <div className="mt-4">
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    )
  }

  if (!drop) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold">Drop not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This drop may have been deleted or you may not have access.
          </p>
          <div className="mt-4">
            <Button asChild variant="outline">
              <Link to="/drops">Back to drops</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Drops', href: '/drops' },
          { label: drop.name },
        ]}
      />

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold truncate">{drop.name}</h1>
          {drop.description && <p className="mt-1 text-muted-foreground">{drop.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/drops/${drop.id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Image className="h-4 w-4" />
            Photos
          </div>
          <div className="mt-2 text-2xl font-bold">{drop.photoCount}</div>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Users className="h-4 w-4" />
            Contributors
          </div>
          <div className="mt-2 text-2xl font-bold">{drop.contributorCount}</div>
        </div>
        <div className="bg-card rounded-xl border p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Eye className="h-4 w-4" />
            Views
          </div>
          <div className="mt-2 text-2xl font-bold">{drop.viewCount}</div>
        </div>
      </div>

      <div className="bg-card rounded-xl border p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold">Contributor link</h2>
            <p className="text-sm text-muted-foreground">Share this link to collect photos.</p>
          </div>
          <Button onClick={handleCopy} disabled={!contributorUrl}>
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>
        {contributorUrl ? (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm font-mono break-all">{contributorUrl}</div>
        ) : (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
            No contributor token found for this drop yet.
          </div>
        )}
      </div>

      {photosLoading ? (
        <div className="bg-card rounded-xl border p-8 text-center">
          <p className="text-sm text-muted-foreground">Loading photos...</p>
        </div>
      ) : photos.length > 0 ? (
        <div className="bg-card rounded-xl border p-5">
          <PhotoGrid photos={photos} dropId={drop.id} dropName={drop.name} />
        </div>
      ) : (
        <div className="bg-card rounded-xl border p-8 text-center">
          <Upload className="h-10 w-10 text-muted-foreground mx-auto" />
          <h3 className="mt-3 font-semibold">Photos will appear here</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Share the contributor link to start collecting photos.
          </p>
        </div>
      )}
      <ContributorInvitationsSection
        dropId={drop.id}
        dropName={drop.name}
        organizationId={drop.organizationId}
        contributorToken={drop.defaultContributorToken ?? null}
      />
      <div className="bg-card rounded-xl border p-5">
        <h3 className="font-semibold mb-4">Recent Activity</h3>
        <SessionActivityFeed dropId={drop.id} />
      </div>
    </div>
  )
}

interface ExtendTimeModalProps {
  dropName: string
  currentExpiresAt?: Date
  onConfirm: (days: number) => void
  onCancel: () => void
}

function ExtendTimeModal({ dropName, currentExpiresAt, onConfirm, onCancel }: ExtendTimeModalProps) {
  const [selectedDays, setSelectedDays] = useState<number>(7)

  const durationOptions = [
    { days: 1, label: '1 day' },
    { days: 3, label: '3 days' },
    { days: 7, label: '1 week' },
    { days: 14, label: '2 weeks' },
    { days: 30, label: '1 month' },
  ]

  // Calculate the new expiry date for preview
  const baseDate = currentExpiresAt ? new Date(currentExpiresAt) : new Date()
  const newExpiresAt = new Date(baseDate)
  newExpiresAt.setDate(newExpiresAt.getDate() + selectedDays)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-primary mb-4">
          <Clock className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Extend Drop Time</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Extend the closing date for <span className="font-medium text-foreground">"{dropName}"</span>.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            How much time would you like to add?
          </label>
          <div className="grid grid-cols-5 gap-2">
            {durationOptions.map((option) => (
              <button
                key={option.days}
                onClick={() => setSelectedDays(option.days)}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  selectedDays === option.days
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary hover:bg-muted/30'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* New expiry date preview */}
        <div className="bg-muted/50 border rounded-lg p-4 mb-6">
          <div className="text-sm">
            <span className="text-muted-foreground">New closing date: </span>
            <span className="font-medium text-foreground">
              {newExpiresAt.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
          {currentExpiresAt && (
            <div className="text-xs text-muted-foreground mt-1">
              Extended from {new Date(currentExpiresAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={() => onConfirm(selectedDays)}
          >
            <Clock className="mr-2 h-4 w-4" />
            Extend Time
          </Button>
        </div>
      </div>
    </div>
  )
}
